import { memo, useEffect, useRef, useState } from 'react'
import { Waypoint } from 'react-waypoint'
import gql from 'graphql-tag'
import pick from 'lodash/fp/pick'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import styled from 'styled-components'
import {
  CoverLetterDetail_CoverLetterFragment,
  CoverLetterDetail_CurrentUserFragment,
} from 'generated/graphql'
import useShowComponentByAppVersion from '@app.feature/activityDetail/hooks/useShowComponentByAppVersion'
import CoverLetterSearchBannerStatusDesktop from '@app.feature/ad/desktop/CoverLetterSearchBanner/CoverLetterSearchBannerStatus.desktop'
import CoverLetterSearchBannerStatusMobile from '@app.feature/ad/mobile/CoverLetterSearchBanner/CoverLetterSearchBannerStatus.mobile'
import useMediaQuery from 'app.modules/hooks/useMediaQuery'
import { mobileBreakPoint } from 'app.styled/media'
import Footer from 'components/Footer/Footer'
import { VERIFIED_APP_VERSIONS } from 'constants/verifyAppVersion/verifyAppVersionList'
import { copySourceToClipboard } from 'utils/utils'
import CoverLetterDetailContent from './CoverLetterDetailContent'
import SelectionGuideBar from './CoverLetterDetailContent/SelectionGuideBar'
import CoverLetterDetailInfo from './CoverLetterDetailInfo'
import NewWindowAndBackButton from './NewWindowAndBackButton'
import { ARTICLE_ID_SELECTOR } from './constant'

export const tabletBreakPoint = '@media (min-width: 960px)'

export interface CoverLetterDetailProps {
  isSimplePage?: boolean
  coverLetter?: CoverLetterDetail_CoverLetterFragment
  currentUser?: CoverLetterDetail_CurrentUserFragment
  listLink?: string
  onScrollEnter?: () => void
  onScrollLeave?: () => void
  onScrollMove?: () => void
  onScrollTop?: () => void
  highlight?: string[] | null | undefined
}

const CoverLetterDetail = ({
  isSimplePage = false,
  listLink,
  coverLetter,
  currentUser,
  onScrollEnter,
  onScrollLeave,
  onScrollTop,
  onScrollMove,
  highlight,
}: CoverLetterDetailProps) => {
  const rootEl = useRef<HTMLDivElement>(null)
  const [isScrollFull, setIsScrollFull] = useState(false)

  // tabletBreakPoint 이상인지 확인
  const isMdUp = useMediaQuery('(min-width: 960px)')

  const coverLetterId = coverLetter?.id
  useEffect(() => {
    if (!coverLetterId || !isMdUp) {
      return
    }
    if (rootEl.current) {
      if (rootEl.current.scrollTo) {
        rootEl.current.scrollTo({ left: 0, top: 0, behavior: 'auto' })
      } else {
        rootEl.current.scrollTop = 0
      }
    }
  }, [coverLetterId])

  const handleEnter: Waypoint.WaypointProps['onEnter'] = ({ event }) => {
    if (onScrollTop) {
      onScrollTop()
    }
    const target: any = get(event, 'target')
    if (target?.scrollHeight === target?.offsetHeight) {
      setIsScrollFull(true)
      return
    }
    setIsScrollFull(false)
    if (onScrollEnter) {
      onScrollEnter()
    }
  }
  const handleLeave: Waypoint.WaypointProps['onLeave'] = () => {
    if (onScrollMove) {
      onScrollMove()
    }

    if (onScrollLeave) {
      onScrollLeave()
    }
  }

  const isComponentVisible = useShowComponentByAppVersion(
    VERIFIED_APP_VERSIONS.COVER_LETTER_AD_EDUCATION,
  )

  return (
    <StyledWrapper
      className={!isSimplePage ? 'scroll' : ''}
      onWheel={isScrollFull ? undefined : (event) => event.stopPropagation()}
      ref={rootEl}
    >
      {isMdUp && !isSimplePage && (
        <Waypoint onEnter={handleEnter} onLeave={handleLeave} />
      )}
      <div className="root">
        <div className="container">
          <CoverLetterDetailInfo
            isSimplePage={isSimplePage}
            listLink={listLink}
            coverLetter={coverLetter ?? undefined}
            currentUser={currentUser ?? undefined}
          />
        </div>
      </div>
      {/* 자소서 문장 스크랩 가이드(모바일) */}
      <div className="selection-guide">
        <SelectionGuideBar currentUser={currentUser} />
      </div>
      {/* 콘텐츠 */}
      <div className={`content-wrapper ${isSimplePage ? 'simple-page' : ''}`}>
        {isMdUp ? (
          <CoverLetterSearchBannerStatusDesktop />
        ) : (
          isComponentVisible && <CoverLetterSearchBannerStatusMobile />
        )}
        <main id={ARTICLE_ID_SELECTOR} onCopy={copySourceToClipboard}>
          <CoverLetterDetailContent
            coverLetter={coverLetter}
            highlight={highlight?.[0]}
            currentUser={currentUser ?? undefined}
          />
        </main>

        <div className="bottom-buttons-wrapper">
          <NewWindowAndBackButton
            backButtonLink={listLink || ''}
            isSimplePage={isSimplePage}
          />
        </div>
        <HiddenSmDown>{isSimplePage && <Footer />}</HiddenSmDown>
      </div>
    </StyledWrapper>
  )
}

const pickProps = pick([
  'isSimplePage',
  'coverLetter',
  'currentUser',
  'listLink',
])

export default memo(CoverLetterDetail, (prevProps, nextProps) => {
  return isEqual(pickProps(prevProps), pickProps(nextProps))
})

gql`
  fragment CoverLetterDetail_currentUser on User {
    ...CoverLetterListItem_currentUser
    ...CoverLetterDetailInfo_currentUser
  }
  fragment CoverLetterDetail_coverLetter on CoverLetter {
    id
    content
    ...CoverLetterListItem_coverLetter
    ...CoverLetterDetailInfo_coverLetter
  }
`

const StyledWrapper = styled.div`
  &.scroll {
    ${tabletBreakPoint} {
      max-width: 660px;
      width: 100%;
      flex: 1 1 0px;
      overflow-y: auto;
    }
  }

  > .root {
    position: relative;
    padding: 30px 20px 0 30px;
    ${mobileBreakPoint} {
      background-color: #ffffff;
      padding: 30px 20px 0 20px;
      margin-bottom: 15px;
    }
  }

  .selection-guide {
    padding: 30px 20px 0 30px;
    ${mobileBreakPoint} {
      padding: 0;
    }
  }

  .container {
    border-bottom: 1px solid #eee;
    ${mobileBreakPoint} {
      border-bottom: none;
    }
  }

  .content-wrapper {
    padding: 30px 20px 30px 30px;
    margin-bottom: 50px;
    ${mobileBreakPoint} {
      padding: 20px 20px 30px 20px;
      margin-top: 15px;
      margin-bottom: 30px;
      background-color: #ffffff;
    }
  }

  .bottom-buttons-wrapper {
    ${tabletBreakPoint} {
      padding-bottom: 50px;
    }
  }

  .simple-page {
    margin-bottom: 0;
  }

  .loading-container {
    display: flex;
    flex-grow: 1;
    justify-content: center;
    align-items: center;
  }
`

const HiddenSmDown = styled.div`
  ${mobileBreakPoint} {
    display: none;
  }
`
